(() => {
  'use strict';

  const GALLERY_SELECTOR =
    'slide-with-thumbs[data-dji-hover-gallery="true"]';

  const DESKTOP_MEDIA = window.matchMedia('(min-width: 768px)');

  const RETRY_LIMIT = 160;
  const RETRY_DELAY = 50;

  const MAIN_SPEED = 220;
  const THUMB_SPEED = 190;
  const FILTER_SPEED = 180;
  const THUMB_GAP = 14;
  const THUMB_SAFE_SPACE = 14;

  const MOBILE_INTRO_DELAYS = [30, 90, 180, 320, 520, 800, 1150, 1500];

  function normalizeFilter(value) {
    const filter = String(value || '').toLowerCase();

    if (
      filter === 'video' ||
      filter === 'videos' ||
      filter === 'intro'
    ) {
      return 'video';
    }

    return 'image';
  }

  function getMainSwiper(gallery) {
    return (
      gallery.globalSlide ||
      gallery.querySelector('.swiper-wrapper-preview')?.swiper ||
      gallery.querySelector('.media-main-swiper')?.swiper ||
      null
    );
  }

  function getThumbSwiper(gallery) {
    return (
      gallery.thumbnailSlide ||
      gallery.querySelector('.dji-gallery__thumbs-swiper')?.swiper ||
      null
    );
  }

  function getMediaType(slide) {
    if (!slide) {
      return 'other';
    }

    const declaredType = String(
      slide.dataset.mediaType ||
        slide.getAttribute('data-media-type') ||
        slide.dataset.media ||
        slide.getAttribute('data-media') ||
        ''
    ).toLowerCase();

    if (
      declaredType === 'video' ||
      declaredType === 'external_video' ||
      declaredType === 'external-video'
    ) {
      return 'video';
    }

    if (
      slide.classList.contains('media-gallery__local-video') ||
      slide.classList.contains('media-gallery__external-video') ||
      slide.classList.contains('media-gallery__external_video') ||
      slide.querySelector('video') ||
      slide.querySelector('iframe')
    ) {
      return 'video';
    }

    if (
      declaredType === 'image' ||
      slide.classList.contains('media-gallery__image')
    ) {
      return 'image';
    }

    return 'other';
  }

  function isVideoSlide(slide) {
    return getMediaType(slide) === 'video';
  }

  function matchesFilter(pair, type) {
    return type === 'video'
      ? pair.type === 'video'
      : pair.type === 'image';
  }

  function updateSwiper(swiper) {
    if (!swiper || swiper.destroyed) {
      return;
    }

    swiper.updateSize?.();
    swiper.updateSlides?.();
    swiper.updateProgress?.();
    swiper.updateSlidesClasses?.();
    swiper.navigation?.update?.();
    swiper.pagination?.update?.();
    swiper.update?.();
  }

  function getPosterUrl(mainSlide, thumbSlide) {
    const localVideo = mainSlide?.querySelector('video');
    const localPoster = localVideo?.getAttribute('poster');

    if (localPoster) {
      return localPoster;
    }

    const thumbImage = thumbSlide?.querySelector('img');

    if (thumbImage) {
      return (
        thumbImage.currentSrc ||
        thumbImage.src ||
        thumbImage.getAttribute('src') ||
        ''
      );
    }

    const mainImage = mainSlide?.querySelector('img');

    if (mainImage) {
      return (
        mainImage.currentSrc ||
        mainImage.src ||
        mainImage.getAttribute('src') ||
        ''
      );
    }

    return '';
  }

  function sendIframeCommand(iframe, command) {
    if (!iframe?.contentWindow) {
      return;
    }

    const source = iframe.getAttribute('src') || '';

    try {
      if (
        source.includes('youtube.com') ||
        source.includes('youtu.be')
      ) {
        iframe.contentWindow.postMessage(
          JSON.stringify({
            event: 'command',
            func: command,
            args: []
          }),
          '*'
        );
      } else if (source.includes('vimeo.com')) {
        iframe.contentWindow.postMessage(
          JSON.stringify({
            method: command === 'playVideo' ? 'play' : 'pause'
          }),
          '*'
        );
      }
    } catch (error) {
      /* Ignore temporary player errors. */
    }
  }

  function normaliseIframeAutoplay(iframe) {
    if (
      !iframe ||
      iframe.dataset.djiAutoplayNormalised === 'true'
    ) {
      return;
    }

    const source = iframe.getAttribute('src');

    if (!source) {
      return;
    }

    try {
      const url = new URL(source, window.location.origin);

      if (url.hostname.includes('youtube.com')) {
        url.searchParams.set('enablejsapi', '1');
        url.searchParams.set('autoplay', '0');
        url.searchParams.set('mute', '0');
        url.searchParams.set('playsinline', '1');
      } else if (url.hostname.includes('vimeo.com')) {
        url.searchParams.set('autoplay', '0');
        url.searchParams.set('muted', '0');
      }

      iframe.setAttribute('src', url.toString());
    } catch (error) {
      /* Preserve the original URL. */
    }

    iframe.dataset.djiAutoplayNormalised = 'true';
  }

  function resetVideoSlide(slide, resetTime = true) {
    if (!isVideoSlide(slide)) {
      return;
    }

    slide.classList.remove('is-dji-video-playing');

    const posterButton = slide.querySelector('.dji-video-poster');

    if (posterButton) {
      posterButton.hidden = false;
      posterButton.style.display = '';
      posterButton.setAttribute('aria-hidden', 'false');
    }

    const video = slide.querySelector('video');

    if (video) {
      try {
        video.pause();
        video.autoplay = false;
        video.removeAttribute('autoplay');
        video.controls = false;

        if (resetTime && Number.isFinite(video.duration)) {
          video.currentTime = 0;
        }
      } catch (error) {
        /* Ignore temporary video errors. */
      }
    }

    const iframe = slide.querySelector('iframe');

    if (iframe) {
      sendIframeCommand(iframe, 'pauseVideo');
    }
  }

  function playVideoSlide(slide) {
    if (!isVideoSlide(slide)) {
      return;
    }

    slide.classList.add('is-dji-video-playing');

    const posterButton = slide.querySelector('.dji-video-poster');

    if (posterButton) {
      posterButton.hidden = true;
      posterButton.style.display = 'none';
      posterButton.setAttribute('aria-hidden', 'true');
    }

    const video = slide.querySelector('video');

    if (video) {
      video.autoplay = false;
      video.removeAttribute('autoplay');
      video.controls = true;

      const playPromise = video.play();

      if (
        playPromise &&
        typeof playPromise.catch === 'function'
      ) {
        playPromise.catch(() => {
          resetVideoSlide(slide, false);
        });
      }

      return;
    }

    const iframe = slide.querySelector('iframe');

    if (iframe) {
      sendIframeCommand(iframe, 'playVideo');

      window.setTimeout(() => {
        sendIframeCommand(iframe, 'playVideo');
      }, 140);
    }
  }

  function createPosterButton(mainSlide, posterUrl, index) {
    let button = mainSlide.querySelector('.dji-video-poster');

    if (button) {
      const existingImage = button.querySelector('img');

      if (
        existingImage &&
        posterUrl &&
        !existingImage.getAttribute('src')
      ) {
        existingImage.src = posterUrl;
      }

      return button;
    }

    button = document.createElement('button');
    button.type = 'button';
    button.className = 'dji-video-poster';

    button.setAttribute(
      'aria-label',
      `Play product video ${index + 1}`
    );

    button.setAttribute('aria-hidden', 'false');

    if (posterUrl) {
      const image = document.createElement('img');

      image.className = 'dji-video-poster__image';
      image.src = posterUrl;
      image.alt = '';
      image.loading = 'eager';
      image.decoding = 'async';

      button.appendChild(image);
    }

    const playIcon = document.createElement('span');

    playIcon.className = 'dji-video-poster__play';
    playIcon.setAttribute('aria-hidden', 'true');

    playIcon.innerHTML = `
      <svg
        viewBox="0 0 24 24"
        width="28"
        height="28"
        aria-hidden="true"
      >
        <path
          d="M8.5 6.8v10.4c0 .9 1 1.4 1.7.9l7.8-5.2c.7-.5.7-1.5 0-2l-7.8-5.2c-.7-.5-1.7 0-1.7 1.1Z"
          fill="currentColor"
        />
      </svg>
    `;

    button.appendChild(playIcon);

    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      playVideoSlide(mainSlide);
    });

    mainSlide.appendChild(button);

    return button;
  }

  function collectOriginalPairs(mainWrapper, thumbWrapper) {
    const originalMainSlides = Array.from(mainWrapper.children).filter(
      (slide) => slide.classList.contains('swiper-slide')
    );

    const originalThumbSlides = Array.from(thumbWrapper.children).filter(
      (slide) => slide.classList.contains('swiper-slide')
    );

    return originalMainSlides.map((mainSlide, index) => ({
      mainSlide,
      thumbSlide: originalThumbSlides[index] || null,
      type: getMediaType(mainSlide),
      originalIndex: index
    }));
  }

  function prepareVideoPosters(pairs) {
    pairs.forEach((pair, index) => {
      const { mainSlide, thumbSlide, type } = pair;

      mainSlide.dataset.djiOriginalIndex = String(index);
      mainSlide.dataset.djiMediaType = type;

      if (thumbSlide) {
        thumbSlide.dataset.djiOriginalIndex = String(index);
        thumbSlide.dataset.djiMediaType = type;

        thumbSlide.classList.toggle(
          'dji-gallery__video-thumb',
          type === 'video'
        );

        thumbSlide.classList.toggle(
          'dji-gallery__image-thumb',
          type === 'image'
        );
      }

      if (type !== 'video') {
        return;
      }

      mainSlide.classList.add('dji-gallery__video-slide');

      const posterUrl = getPosterUrl(mainSlide, thumbSlide);

      createPosterButton(mainSlide, posterUrl, index);

      const video = mainSlide.querySelector('video');

      if (video) {
        video.autoplay = false;
        video.removeAttribute('autoplay');
        video.controls = false;
        video.preload = 'metadata';

        if (video.dataset.djiPlayGuard !== 'true') {
          video.dataset.djiPlayGuard = 'true';

          video.addEventListener('play', () => {
            if (
              !mainSlide.classList.contains('is-dji-video-playing')
            ) {
              try {
                video.pause();
              } catch (error) {
                /* Ignore playback timing errors. */
              }
            }
          });
        }
      }

      const iframe = mainSlide.querySelector('iframe');

      if (iframe) {
        normaliseIframeAutoplay(iframe);
      }

      resetVideoSlide(mainSlide, false);
    });
  }

  function isVisibleSlide(slide) {
    if (!slide || slide.hidden || slide.classList.contains('is-hidden')) {
      return false;
    }

    if (slide.style.display === 'none') {
      return false;
    }

    return true;
  }

  function getFirstVideoIndex(slides) {
    let firstVisibleVideoIndex = -1;
    let firstVideoIndex = -1;

    slides.forEach((slide, index) => {
      if (!isVideoSlide(slide)) {
        return;
      }

      if (firstVideoIndex === -1) {
        firstVideoIndex = index;
      }

      if (firstVisibleVideoIndex === -1 && isVisibleSlide(slide)) {
        firstVisibleVideoIndex = index;
      }
    });

    return firstVisibleVideoIndex !== -1
      ? firstVisibleVideoIndex
      : firstVideoIndex;
  }

  function resetAllVideos(root) {
    root.querySelectorAll('.swiper-slide').forEach((slide) => {
      if (isVideoSlide(slide)) {
        resetVideoSlide(slide, true);
      }
    });
  }

  function forceMobileFirstIntro(gallery, mainSwiper) {
    if (!gallery || !mainSwiper || mainSwiper.destroyed) {
      return;
    }

    const freshSlides = Array.from(mainSwiper.slides || []);
    const firstVideoIndex = getFirstVideoIndex(freshSlides);

    if (firstVideoIndex < 0) {
      return;
    }

    const firstVideoSlide = freshSlides[firstVideoIndex];

    resetAllVideos(gallery);
    resetVideoSlide(firstVideoSlide, false);

    updateSwiper(mainSwiper);

    if (
      typeof mainSwiper.slideToLoop === 'function' &&
      mainSwiper.params?.loop
    ) {
      mainSwiper.slideToLoop(firstVideoIndex, 0, false);
    } else {
      mainSwiper.slideTo(firstVideoIndex, 0, false);
    }

    mainSwiper.updateSlidesClasses?.();

    window.requestAnimationFrame(() => {
      resetVideoSlide(firstVideoSlide, false);
      updateSwiper(mainSwiper);

      if (
        typeof mainSwiper.slideToLoop === 'function' &&
        mainSwiper.params?.loop
      ) {
        mainSwiper.slideToLoop(firstVideoIndex, 0, false);
      } else {
        mainSwiper.slideTo(firstVideoIndex, 0, false);
      }

      mainSwiper.updateSlidesClasses?.();
    });
  }

  function keepMobileIntroOnFirstVideo(gallery, mainSwiper) {
    gallery.dataset.djiMobileForceIntro = 'true';

    MOBILE_INTRO_DELAYS.forEach((delay) => {
      window.setTimeout(() => {
        if (gallery.dataset.djiMobileForceIntro === 'true') {
          forceMobileFirstIntro(gallery, mainSwiper);
        }
      }, delay);
    });

    window.setTimeout(() => {
      gallery.dataset.djiMobileForceIntro = 'false';
    }, 1800);
  }

  function initialiseMobileSafe(gallery, attempt = 0) {
    if (
      !gallery ||
      gallery.dataset.djiMobileSafeReady === 'true'
    ) {
      return;
    }

    const mainSwiper = getMainSwiper(gallery);

    const mainSwiperElement = gallery.querySelector(
      '.swiper-wrapper-preview'
    );

    const mainWrapper = mainSwiperElement?.querySelector(
      ':scope > .swiper-wrapper'
    );

    const thumbWrapper = gallery
      .querySelector('.dji-gallery__thumbs-swiper')
      ?.querySelector(':scope > .swiper-wrapper');

    if (!mainSwiper || !mainWrapper || !thumbWrapper) {
      if (attempt < RETRY_LIMIT) {
        window.setTimeout(() => {
          initialiseMobileSafe(gallery, attempt + 1);
        }, RETRY_DELAY);
      }

      return;
    }

    gallery.dataset.djiMobileSafeReady = 'true';
    gallery.dataset.djiMobileForceIntro = 'false';

    const allPairs = collectOriginalPairs(mainWrapper, thumbWrapper);

    prepareVideoPosters(allPairs);

    const section = gallery.closest('[id^="MainProduct-"]') || document;

    section.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest('.js-media-filter');

        if (
          !button ||
          !section.contains(button) ||
          DESKTOP_MEDIA.matches
        ) {
          return;
        }

        const filter = normalizeFilter(button.dataset.filter);

        if (filter === 'video') {
          keepMobileIntroOnFirstVideo(gallery, mainSwiper);
        } else {
          gallery.dataset.djiMobileForceIntro = 'false';
          resetAllVideos(gallery);
        }
      },
      false
    );

    mainSwiper.on?.('slideChange', () => {
      if (
        !DESKTOP_MEDIA.matches &&
        gallery.dataset.djiMobileForceIntro === 'true'
      ) {
        window.setTimeout(() => {
          forceMobileFirstIntro(gallery, mainSwiper);
        }, 30);
      }
    });
  }

  function initialiseDesktopGallery(gallery, attempt = 0) {
    if (
      !gallery ||
      gallery.dataset.djiGalleryStableReady === 'true'
    ) {
      return;
    }

    if (!DESKTOP_MEDIA.matches) {
      initialiseMobileSafe(gallery, attempt);
      return;
    }

    const mainSwiper = getMainSwiper(gallery);
    const thumbSwiper = getThumbSwiper(gallery);

    const mainSwiperElement = gallery.querySelector(
      '.swiper-wrapper-preview'
    );

    const mainWrapper = mainSwiperElement?.querySelector(
      ':scope > .swiper-wrapper'
    );

    const thumbSwiperElement = gallery.querySelector(
      '.dji-gallery__thumbs-swiper'
    );

    const thumbWrapper = thumbSwiperElement?.querySelector(
      ':scope > .swiper-wrapper'
    );

    const mainArea = gallery.querySelector('.media-main-swiper');
    const thumbTray = gallery.querySelector('.dji-gallery__thumbs');
    const dots = gallery.querySelector('.dji-gallery__dots');

    if (
      !mainSwiper ||
      !thumbSwiper ||
      !mainWrapper ||
      !thumbWrapper ||
      !mainArea ||
      !thumbTray ||
      !dots
    ) {
      if (attempt < RETRY_LIMIT) {
        window.setTimeout(() => {
          initialiseDesktopGallery(gallery, attempt + 1);
        }, RETRY_DELAY);
      }

      return;
    }

    gallery.dataset.djiGalleryStableReady = 'true';

    const allPairs = collectOriginalPairs(mainWrapper, thumbWrapper);

    let visiblePairs = [];
    let dotButtons = [];
    let closeTimer = 0;
    let filterToken = 0;
    let desktopForceFirstActive = false;

    mainSwiper.params.speed = MAIN_SPEED;
    mainSwiper.params.preventInteractionOnTransition = false;

    thumbSwiper.params.speed = THUMB_SPEED;
    thumbSwiper.params.slidesPerView = 'auto';
    thumbSwiper.params.spaceBetween = THUMB_GAP;
    thumbSwiper.params.centeredSlides = false;
    thumbSwiper.params.centeredSlidesBounds = false;
    thumbSwiper.params.centerInsufficientSlides = false;
    thumbSwiper.params.slidesOffsetBefore = 0;
    thumbSwiper.params.slidesOffsetAfter = 0;
    thumbSwiper.params.watchSlidesProgress = true;
    thumbSwiper.params.preventInteractionOnTransition = false;

    prepareVideoPosters(allPairs);

    function setFilterButtonState(type) {
      const section = gallery.closest('[id^="MainProduct-"]') || document;

      section.querySelectorAll('.js-media-filter').forEach((button) => {
        const buttonFilter = normalizeFilter(button.dataset.filter);
        const active = buttonFilter === type;

        button.classList.toggle('is-active', active);

        button.setAttribute(
          'aria-pressed',
          active ? 'true' : 'false'
        );
      });
    }

    function resetAllVideos() {
      allPairs.forEach((pair) => {
        if (pair.type === 'video') {
          resetVideoSlide(pair.mainSlide, true);
        }
      });
    }

    function showPair(pair, index) {
      pair.mainSlide.hidden = false;
      pair.mainSlide.style.display = '';
      pair.mainSlide.classList.remove('is-hidden');
      pair.mainSlide.setAttribute('aria-hidden', 'false');
      pair.mainSlide.dataset.djiVisibleIndex = String(index);

      if (pair.thumbSlide) {
        pair.thumbSlide.hidden = false;
        pair.thumbSlide.style.display = '';
        pair.thumbSlide.classList.remove('is-hidden');
        pair.thumbSlide.setAttribute('aria-hidden', 'false');
        pair.thumbSlide.dataset.djiVisibleIndex = String(index);
      }
    }

    function hidePair(pair) {
      pair.mainSlide.hidden = true;
      pair.mainSlide.style.display = 'none';
      pair.mainSlide.classList.add('is-hidden');
      pair.mainSlide.setAttribute('aria-hidden', 'true');
      pair.mainSlide.removeAttribute('data-dji-visible-index');

      if (pair.type === 'video') {
        resetVideoSlide(pair.mainSlide, true);
      }

      if (pair.thumbSlide) {
        pair.thumbSlide.hidden = true;
        pair.thumbSlide.style.display = 'none';
        pair.thumbSlide.classList.add('is-hidden');
        pair.thumbSlide.setAttribute('aria-hidden', 'true');
        pair.thumbSlide.removeAttribute('data-dji-visible-index');
      }
    }

    function reorderSlidesForFilter(matchingPairs) {
      const matchingSet = new Set(matchingPairs);
      const hiddenPairs = allPairs.filter((pair) => !matchingSet.has(pair));

      matchingPairs.forEach((pair, index) => {
        mainWrapper.appendChild(pair.mainSlide);

        if (pair.thumbSlide) {
          thumbWrapper.appendChild(pair.thumbSlide);
        }

        showPair(pair, index);
      });

      hiddenPairs.forEach((pair) => {
        mainWrapper.appendChild(pair.mainSlide);

        if (pair.thumbSlide) {
          thumbWrapper.appendChild(pair.thumbSlide);
        }

        hidePair(pair);
      });

      updateSwiper(mainSwiper);
      updateSwiper(thumbSwiper);

      return matchingPairs;
    }

    function getVisibleThumbSlides() {
      return visiblePairs
        .map((pair) => pair.thumbSlide)
        .filter(Boolean);
    }

    function getThumbMetrics() {
      const slides = getVisibleThumbSlides();

      if (!slides.length) {
        return {
          slides,
          fits: true,
          totalWidth: 0,
          availableWidth: 0
        };
      }

      const style = window.getComputedStyle(thumbSwiperElement);
      const paddingLeft = parseFloat(style.paddingLeft) || 0;
      const paddingRight = parseFloat(style.paddingRight) || 0;

      const availableWidth =
        thumbSwiperElement.clientWidth - paddingLeft - paddingRight;

      const first = slides[0];
      const last = slides[slides.length - 1];

      const totalWidth =
        last.offsetLeft + last.offsetWidth - first.offsetLeft;

      return {
        slides,
        fits: totalWidth <= availableWidth + 1,
        totalWidth,
        availableWidth
      };
    }

    function setThumbnailAlignment() {
      window.requestAnimationFrame(() => {
        const metrics = getThumbMetrics();

        if (!metrics.slides.length) {
          return;
        }

        const trayPadding = 16;
        const maxTrayWidth =
          thumbTray.parentElement?.clientWidth
            ? thumbTray.parentElement.clientWidth - 96
            : thumbSwiperElement.clientWidth;

        const wantedWidth = metrics.fits
          ? metrics.totalWidth + trayPadding
          : Math.min(maxTrayWidth, thumbSwiperElement.clientWidth);

        thumbTray.style.width = `${Math.max(0, Math.round(wantedWidth))}px`;
        thumbTray.style.maxWidth = 'calc(100% - 96px)';

        thumbWrapper.style.justifyContent = metrics.fits
          ? 'center'
          : 'flex-start';

        updateSwiper(thumbSwiper);

        if (metrics.fits) {
          thumbSwiper.setTransition?.(0);
          thumbSwiper.setTranslate?.(0);
          thumbSwiper.updateProgress?.(0);
          thumbSwiper.updateActiveIndex?.(0);
          thumbSwiper.updateSlidesClasses?.();
        }
      });
    }

    function syncThumbActive(index) {
      getVisibleThumbSlides().forEach((thumb, thumbIndex) => {
        thumb.classList.toggle(
          'swiper-slide-thumb-active',
          thumbIndex === index
        );
      });

      thumbSwiper.updateSlidesClasses?.();
    }

    function revealThumb(index, speed = THUMB_SPEED) {
      if (
        !thumbSwiper ||
        thumbSwiper.destroyed ||
        index < 0 ||
        index >= visiblePairs.length
      ) {
        return;
      }

      window.requestAnimationFrame(() => {
        const metrics = getThumbMetrics();
        const slides = metrics.slides;
        const target = slides[index];

        if (!target) {
          return;
        }

        if (metrics.fits) {
          thumbWrapper.style.justifyContent = 'center';
          thumbSwiper.setTransition?.(speed);
          thumbSwiper.setTranslate?.(0);
          thumbSwiper.updateProgress?.(0);
          thumbSwiper.updateActiveIndex?.(0);
          thumbSwiper.updateSlidesClasses?.();
          return;
        }

        thumbWrapper.style.justifyContent = 'flex-start';

        const style = window.getComputedStyle(thumbSwiperElement);
        const paddingLeft = parseFloat(style.paddingLeft) || 0;
        const paddingRight = parseFloat(style.paddingRight) || 0;
        const containerWidth =
          thumbSwiperElement.clientWidth - paddingLeft - paddingRight;

        const first = slides[0];
        const last = slides[slides.length - 1];

        const firstLeft = first.offsetLeft;
        const contentWidth =
          last.offsetLeft + last.offsetWidth - firstLeft;

        const targetLeft = target.offsetLeft - firstLeft;
        const targetRight = targetLeft + target.offsetWidth;

        const minTranslate = Math.min(0, containerWidth - contentWidth);
        const maxTranslate = 0;

        let nextTranslate = thumbSwiper.getTranslate?.() || 0;

        const visibleLeft = -nextTranslate;
        const visibleRight = visibleLeft + containerWidth;

        if (targetLeft < visibleLeft + THUMB_SAFE_SPACE) {
          nextTranslate = -(targetLeft - THUMB_SAFE_SPACE);
        } else if (targetRight > visibleRight - THUMB_SAFE_SPACE) {
          nextTranslate = -(targetRight - containerWidth + THUMB_SAFE_SPACE);
        }

        nextTranslate = Math.min(
          maxTranslate,
          Math.max(minTranslate, nextTranslate)
        );

        thumbSwiper.setTransition?.(speed);
        thumbSwiper.setTranslate?.(nextTranslate);
        thumbSwiper.updateProgress?.(nextTranslate);
        thumbSwiper.updateActiveIndex?.();
        thumbSwiper.updateSlidesClasses?.();
      });
    }

    function updateActiveDot() {
      const activeIndex = Math.min(
        Math.max(mainSwiper.activeIndex || 0, 0),
        Math.max(visiblePairs.length - 1, 0)
      );

      dotButtons.forEach((button, index) => {
        const active = index === activeIndex;

        button.classList.toggle('is-active', active);

        button.setAttribute(
          'aria-selected',
          active ? 'true' : 'false'
        );

        button.tabIndex = active ? 0 : -1;
      });
    }

    function buildDots() {
      dots.replaceChildren();
      dotButtons = [];

      visiblePairs.forEach((pair, index) => {
        const button = document.createElement('button');

        button.type = 'button';
        button.className = 'dji-gallery__dot';
        button.setAttribute('role', 'tab');

        button.setAttribute(
          'aria-label',
          `Go to product media ${index + 1}`
        );

        button.addEventListener('click', () => {
          navigateToVisibleIndex(index, MAIN_SPEED);
        });

        dots.appendChild(button);
        dotButtons.push(button);
      });

      dots.hidden = dotButtons.length <= 1;
      dots.classList.toggle('has-dots', dotButtons.length > 1);

      updateActiveDot();
    }

    function navigateToVisibleIndex(index, speed = MAIN_SPEED) {
      const targetPair = visiblePairs[index];

      if (!targetPair) {
        return;
      }

      const currentIndex = Math.min(
        Math.max(mainSwiper.activeIndex || 0, 0),
        Math.max(visiblePairs.length - 1, 0)
      );

      const currentPair = visiblePairs[currentIndex];

      if (
        currentPair &&
        currentPair !== targetPair &&
        currentPair.type === 'video'
      ) {
        resetVideoSlide(currentPair.mainSlide, true);
      }

      if (targetPair.type === 'video') {
        resetVideoSlide(targetPair.mainSlide, false);
      }

      syncThumbActive(index);
      revealThumb(index, Math.min(speed, THUMB_SPEED));

      if (currentIndex === index) {
        mainSwiper.updateSlidesClasses?.();
        updateActiveDot();
        return;
      }

      mainSwiper.slideTo(index, speed, true);
    }

    function forceDesktopFirstVisible(speed = 0) {
      if (!visiblePairs.length || !mainSwiper || mainSwiper.destroyed) {
        return;
      }

      const firstPair = visiblePairs[0];

      if (!firstPair) {
        return;
      }

      if (firstPair.type === 'video') {
        resetAllVideos();
        resetVideoSlide(firstPair.mainSlide, false);
      }

      updateSwiper(mainSwiper);
      updateSwiper(thumbSwiper);

      const freshSlides = Array.from(mainSwiper.slides || []);
      const targetIndex = freshSlides.indexOf(firstPair.mainSlide);
      const slideIndex = targetIndex >= 0 ? targetIndex : 0;

      mainSwiper.setTransition?.(0);
      mainSwiper.slideTo(slideIndex, speed, false);
      mainSwiper.updateActiveIndex?.(slideIndex);
      mainSwiper.updateSlidesClasses?.();

      syncThumbActive(0);
      setThumbnailAlignment();
      revealThumb(0, 0);
      updateActiveDot();
    }

    function scheduleDesktopFirstVisibleCorrection() {
      desktopForceFirstActive = true;

      [0, 40, 90, 180, 320, 520, 760, 1050].forEach((delay) => {
        window.setTimeout(() => {
          if (desktopForceFirstActive) {
            forceDesktopFirstVisible(0);
          }
        }, delay);
      });

      window.setTimeout(() => {
        desktopForceFirstActive = false;
      }, 1250);
    }

    function applyFilter(requestedType, animate = true) {
      const type = normalizeFilter(requestedType);

      const matchingPairs = allPairs.filter((pair) =>
        matchesFilter(pair, type)
      );

      if (!matchingPairs.length) {
        gallery.classList.remove('is-dji-filter-changing');
        return;
      }

      filterToken += 1;
      const token = filterToken;

      setFilterButtonState(type);
      resetAllVideos();

      gallery.classList.add('is-dji-filter-changing');

      visiblePairs = reorderSlidesForFilter(matchingPairs);

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (token !== filterToken) {
            return;
          }

          updateSwiper(mainSwiper);
          updateSwiper(thumbSwiper);

          mainSwiper.setTransition?.(0);
          mainSwiper.setTranslate?.(0);
          mainSwiper.updateProgress?.(0);
          mainSwiper.updateActiveIndex?.(0);
          mainSwiper.updateSlidesClasses?.();

          thumbSwiper.setTransition?.(0);
          thumbSwiper.setTranslate?.(0);
          thumbSwiper.updateProgress?.(0);
          thumbSwiper.updateActiveIndex?.(0);
          thumbSwiper.updateSlidesClasses?.();

          mainSwiper.slideTo(0, animate ? FILTER_SPEED : 0, false);
          thumbSwiper.slideTo(0, 0, false);

          syncThumbActive(0);
          buildDots();
          setThumbnailAlignment();

          const firstPair = visiblePairs[0];

          if (firstPair?.type === 'video') {
            resetVideoSlide(firstPair.mainSlide, false);
          }

          forceDesktopFirstVisible(0);

          if (type === 'video') {
            scheduleDesktopFirstVisibleCorrection();
          }

          window.setTimeout(
            () => {
              if (token !== filterToken) {
                return;
              }

              gallery.classList.remove('is-dji-filter-changing');

              updateSwiper(mainSwiper);
              updateSwiper(thumbSwiper);

              forceDesktopFirstVisible(0);
            },
            animate ? FILTER_SPEED + 80 : 0
          );
        });
      });
    }

    function openThumbnailTray() {
      if (!DESKTOP_MEDIA.matches) {
        return;
      }

      window.clearTimeout(closeTimer);
      gallery.classList.add('is-dji-thumbs-open');

      window.requestAnimationFrame(() => {
        updateSwiper(thumbSwiper);
        setThumbnailAlignment();
        revealThumb(mainSwiper.activeIndex || 0, 0);
      });
    }

    function closeThumbnailTray() {
      window.clearTimeout(closeTimer);

      closeTimer = window.setTimeout(() => {
        if (
          !gallery.matches(':hover') &&
          !gallery.contains(document.activeElement)
        ) {
          gallery.classList.remove('is-dji-thumbs-open');
        }
      }, 120);
    }

    function handleThumbClick(event) {
      const thumb = event.target.closest(
        '.dji-gallery__thumbs-swiper .swiper-slide'
      );

      if (
        !thumb ||
        !gallery.contains(thumb) ||
        thumb.hidden ||
        thumb.classList.contains('is-hidden')
      ) {
        return;
      }

      const visibleIndex = Number(thumb.dataset.djiVisibleIndex);

      if (
        !Number.isInteger(visibleIndex) ||
        visibleIndex < 0 ||
        visibleIndex >= visiblePairs.length
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      desktopForceFirstActive = false;
      navigateToVisibleIndex(visibleIndex, MAIN_SPEED);
    }

    const section = gallery.closest('[id^="MainProduct-"]') || document;

    section.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest('.js-media-filter');

        if (
          !button ||
          !section.contains(button) ||
          !DESKTOP_MEDIA.matches
        ) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        applyFilter(button.dataset.filter, true);
      },
      true
    );

    mainArea.addEventListener('pointerenter', openThumbnailTray);
    mainArea.addEventListener('pointerleave', closeThumbnailTray);
    thumbTray.addEventListener('pointerenter', openThumbnailTray);
    thumbTray.addEventListener('pointerleave', closeThumbnailTray);
    thumbTray.addEventListener('click', handleThumbClick, true);
    gallery.addEventListener('focusin', openThumbnailTray);
    gallery.addEventListener('focusout', closeThumbnailTray);

    mainSwiper.on('slideChange', () => {
      if (!visiblePairs.length) {
        return;
      }

      let activeIndex = mainSwiper.activeIndex || 0;

      if (activeIndex >= visiblePairs.length) {
        activeIndex = visiblePairs.length - 1;
        mainSwiper.slideTo(activeIndex, 0, false);
      }

      activeIndex = Math.max(activeIndex, 0);

      syncThumbActive(activeIndex);
      revealThumb(activeIndex, THUMB_SPEED);
      updateActiveDot();

      visiblePairs.forEach((pair, index) => {
        if (index !== activeIndex && pair.type === 'video') {
          resetVideoSlide(pair.mainSlide, true);
        }
      });
    });

    mainSwiper.on('slideChangeTransitionEnd', () => {
      if (!visiblePairs.length) {
        return;
      }

      const activeIndex = Math.min(
        Math.max(mainSwiper.activeIndex || 0, 0),
        visiblePairs.length - 1
      );

      const activePair = visiblePairs[activeIndex];

      updateActiveDot();

      if (
        activePair?.type === 'video' &&
        !activePair.mainSlide.classList.contains(
          'is-dji-video-playing'
        )
      ) {
        resetVideoSlide(activePair.mainSlide, false);
      }
    });

    const activeButton = section.querySelector('.js-media-filter.is-active');
    const initialFilter = normalizeFilter(activeButton?.dataset.filter);

    applyFilter(initialFilter, false);
  }

  function initialiseAll(root = document) {
    root.querySelectorAll(GALLERY_SELECTOR).forEach((gallery) => {
      if (DESKTOP_MEDIA.matches) {
        initialiseDesktopGallery(gallery);
      } else {
        initialiseMobileSafe(gallery);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initialiseAll();
    });
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialiseAll(event.target);
  });
})();